import {createAsyncThunk, createSlice, Draft, isAnyOf, PayloadAction} from "@reduxjs/toolkit";
import {api} from "../api";
import {
    GameSession,
    QuestionTypeOrder,
    QuestionOrderOption,
    QuestionTypeSelect,
    QuestionTypeConnect, QuestionTextOption, QuestionTypeText, QuestionTypeNumberScale
} from "../../types/gameSession";
import {updateGameTheme} from "./game";
import type {RootState} from '../store'
import {answerFingerprint} from '../gameplayAnswers';
import {FormField} from "../../types/games";

export type GameSessionState = {
    pending: boolean
    activeRequestId: string | null
    requestSessionId: number | null
    baselineAnswerFingerprint: string
    loaded: boolean
    error: string | null
    session_data?: GameSession
    endFormSuccess: string
    transition: boolean
    showPointsDiff: boolean
    redirectUrl: string | null
};

const initialState: GameSessionState = {
    pending: false,
    activeRequestId: null,
    requestSessionId: null,
    baselineAnswerFingerprint: '[]',
    loaded: false,
    error: null,
    session_data: undefined,
    endFormSuccess: '',
    transition: false,
    showPointsDiff: false,
    redirectUrl: null
};

export type NewSessionPayload = {
    form_fields: FormField[] | undefined
    game_id: number
    language: string
}

type SessionThunkAPI = {getState: () => unknown; dispatch: (action: any) => any; requestId: string; rejectWithValue: (value: string) => any};
const sessionState = (getState: () => unknown) => (getState() as RootState).gameSession;
const requestIsCurrent = (thunk: SessionThunkAPI, sessionId: number | null) => {
    const state = sessionState(thunk.getState);
    return state.pending && state.activeRequestId === thunk.requestId && (state.session_data?.id ?? null) === sessionId;
};
const canBegin = (_: unknown, {getState}: {getState: () => unknown}) => !sessionState(getState).pending;
// The shared mutex stays held through the awaited transition.
const requestSession = async (payload: any, thunk: SessionThunkAPI, start = false) => {
    const sessionId = sessionState(thunk.getState).session_data?.id ?? null;
    try {
        let response: GameSession;
        let recovered = false;
        try {
            response = start ? await api.post('/API/games/sessions/', payload) : await api.patch('/API/games/sessions/', payload);
        } catch (error: any) {
            if (start || error.status !== 409 || error.code !== 'stale_content' || !requestIsCurrent(thunk, sessionId)) throw error;
            // Refresh once without replaying the stale mutation.
            response = await api.get('/API/games/sessions/');
            recovered = true;
        }
        if (!requestIsCurrent(thunk, sessionId)) return;
        if (!start && response.id !== sessionId) throw new Error('The game session has changed. Please reload the game.');
        if (!start && !recovered && sessionState(thunk.getState).session_data?.content_id !== response.content_id) {
            thunk.dispatch(fadeOutContent());
            await new Promise(resolve => setTimeout(resolve, 500));
        }
        if (!requestIsCurrent(thunk, sessionId)) return;
        if (response.theme) thunk.dispatch(updateGameTheme(response.theme));
        return response;
    } catch (error: any) { return thunk.rejectWithValue(error.message); }
};

export const startGameSession = createAsyncThunk(
    'gameSession/startGameSession',
    (payload: NewSessionPayload, thunkAPI) => requestSession(payload, thunkAPI, true),
    {condition: canBegin}
);

export type questionAnswerPayload = {
    content_id: number | null
    answers: any[]
    skip?: boolean
}

export type GameSessionIdPayload = {
    game_session_id: number
};

type ContinueGameSessionPayload = {
    content_id: number | null
    back: boolean
};
export const continueGameSession = createAsyncThunk(
    'gameSession/continueGameSession',
    (payload: ContinueGameSessionPayload, thunkAPI) => requestSession({...payload, action: payload.back ? 'back' : 'continue'}, thunkAPI),
    {condition: (payload, thunkAPI) => canBegin(payload, thunkAPI) && sessionState(thunkAPI.getState).session_data?.content_id === payload.content_id}
);


export const pauseGameSession = createAsyncThunk(
    'gameSession/pauseGameSession',
    async (payload: GameSessionIdPayload, thunkAPI) => {
        try {
            return await api.post('/API/games/sessions/pause/', payload);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type EndFormdata = {
    form_fields: FormField[]
    session_id: number
}

export const submitEndForm = createAsyncThunk(
    'gameSession/submitEndForm',
    async (payload: EndFormdata, thunkAPI) => {
        try {
            return await api.post('/API/games/sessions/end/form/', {form: payload});
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const sessionIsAlive = createAsyncThunk(
    'gameSession/sessionIsAlive',
    async (payload: GameSessionIdPayload, thunkAPI) => {
        try {
            return await api.post('/API/games/sessions/keepalive/', payload);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const endGameSession = createAsyncThunk(
    'gameSession/endGameSession',
    async (_, thunkAPI) => {
        try {
            return await api.post('/API/games/sessions/end/', {});
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const questionAnswer = createAsyncThunk(
    'gameSession/questionAnswer',
    (payload: questionAnswerPayload, thunkAPI) => requestSession({...payload, action: 'answer'}, thunkAPI),
    {condition: (payload, thunkAPI) => {
        const state = sessionState(thunkAPI.getState);
        return canBegin(payload, thunkAPI) && state.session_data?.content_id === payload.content_id &&
            !!state.session_data?.question && state.session_data.question.can_edit !== false;
    }}
);

const acceptSession = (state: Draft<GameSessionState>, session: GameSession) => {
    state.session_data = session;
    state.baselineAnswerFingerprint = answerFingerprint(session.question);
    state.loaded = true;
    state.transition = false;
    state.error = null;
};
const canEditAnswer = (state: Draft<GameSessionState>) => !state.pending && state.session_data?.question?.can_edit !== false;


const slice = createSlice({
    name: 'gameSession',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setEndFormData(state, action) {
            if (state.session_data?.end?.form) {
                state.session_data.end.form.forEach((field) => {
                    if (field.name in action.payload) {
                        field.value = action.payload[field.name];
                    }
                });
            }
        },
        updateOrder: (state, action: PayloadAction<QuestionOrderOption[]>) => {
            if (!canEditAnswer(state)) return;
            if (state.session_data) {
                const question = state.session_data.question as QuestionTypeOrder;
                if (question && question.options) {
                    question.options = action.payload;
                }
            }
        },
        updateOptionSelected: (state, action: PayloadAction<{ optionIndex: number, selected: boolean }>) => {
            if (!canEditAnswer(state)) return;
            if (state.session_data) {
                const question = state.session_data.question as QuestionTypeSelect;
                if (question && question.options) {
                    const option = question.options.find(option => option.index === action.payload.optionIndex);
                    if (option) {
                        if (!question.multiple && action.payload.selected) {
                            // If multiple is false and an option is being selected, deselect all other options
                            question.options.forEach(opt => {
                                if (opt !== option) {
                                    opt.selected = false;
                                    opt.answer = '';
                                }
                            });
                        }
                        option.selected = action.payload.selected;
                    }
                }
            }
        },
        updateOptionAnswer: (state, action: PayloadAction<{ optionIndex: number, answer: string, selected: boolean }>) => {
            if (!canEditAnswer(state)) return;
            if (state.session_data) {
                const question = state.session_data.question as QuestionTypeSelect;
                if (question && question.options) {
                    const option = question.options.find(option => option.index === action.payload.optionIndex);
                    if (option) {
                        if (!question.multiple) {
                            question.options.forEach(opt => {
                                if (opt !== option) {
                                    opt.selected = false;
                                    opt.answer = '';
                                }
                            });
                        }
                        option.answer = action.payload.answer;
                        option.selected = action.payload.selected;
                    }
                }
            }
        },
        updateConnectSelectedPairs: (state, action: PayloadAction<{ selected_pairs: [number, number][] }>) => {
            if (!canEditAnswer(state)) return;
            const {selected_pairs} = action.payload;
            const question = state.session_data?.question as QuestionTypeConnect;

            if (question) {
                question.selected_pairs = selected_pairs;
            }
        },

        updateText: (state, action: PayloadAction<QuestionTextOption>) => {
            if (!canEditAnswer(state)) return;
            if (state.session_data) {
                const question = state.session_data.question as QuestionTypeText;
                if (question && question.options) {
                    // Find the index of the option to be updated
                    const optionIndex = question.options.findIndex(option => option.key === action.payload.key);

                    if (optionIndex !== -1) {
                        // Update the option at the found index
                        question.options[optionIndex]['value'] = action.payload.value;
                    }
                }
            }
        },
        updateNumberScale: (state, action: PayloadAction<{ rating: number }>) => {
            if (!canEditAnswer(state)) return;
            if (state.session_data) {
                const question = state.session_data.question as QuestionTypeNumberScale;
                if (question && question.options) {
                    const selectedOption = question.options.find(option => option.rating === action.payload.rating);
                    if (selectedOption) {
                        question.options.forEach(opt => {
                            opt.selected = false;
                        });

                        selectedOption.selected = true;
                    }
                }
            }
        },
        fadeOutContent: (state) => {
            if (state.session_data) {
                state.transition = true;
            }
        },

        showExtraContent: (state, action: PayloadAction<GameSession>) => {
            acceptSession(state, action.payload);
            state.showPointsDiff = true;
        },
        resetPointsDiff: (state) => {
            state.showPointsDiff = false;
        },
        loadNextContent: (state, action: PayloadAction<GameSession>) => {
            acceptSession(state, action.payload);
        },
        setGameSession: (state, action: PayloadAction<GameSession>) => {
            acceptSession(state, action.payload);
            state.pending = false;
            state.activeRequestId = null;
            state.requestSessionId = null;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(submitEndForm.pending, (state, action) => {
            state.loaded = true;
            state.error = null;
        });
        builder.addCase(submitEndForm.fulfilled, (state, action) => {
            if(action.payload.success_message){
                state.endFormSuccess = action.payload.success_message;
            }
        });
       builder.addCase(submitEndForm.rejected, (state, action) => {
            state.loaded = true;
            if (typeof action.payload === 'string') {
                state.error = action.payload as string;
            } else if (typeof action.error?.message === 'string') {
                state.error = action.error.message;
            }
        });
        builder.addCase(endGameSession.fulfilled, (state, action) => {
            state.redirectUrl = action.payload.redirect;
        });
        builder.addMatcher(isAnyOf(startGameSession.pending, questionAnswer.pending, continueGameSession.pending), (state, action) => {
            state.pending = true;
            state.activeRequestId = action.meta.requestId;
            state.requestSessionId = state.session_data?.id ?? null;
            state.error = null;
        });
        builder.addMatcher(isAnyOf(startGameSession.fulfilled, questionAnswer.fulfilled, continueGameSession.fulfilled), (state, action) => {
            if (state.activeRequestId !== action.meta.requestId) return;
            if (action.payload) {
                state.showPointsDiff = questionAnswer.fulfilled.match(action) &&
                    state.session_data?.content_id === action.payload.content_id;
                acceptSession(state, action.payload);
            }
            state.pending = false;
            state.activeRequestId = null;
            state.requestSessionId = null;
        });
        builder.addMatcher(isAnyOf(startGameSession.rejected, questionAnswer.rejected, continueGameSession.rejected), (state, action) => {
            if (state.activeRequestId !== action.meta.requestId) return;
            state.pending = false;
            state.activeRequestId = null;
            state.requestSessionId = null;
            state.transition = false;
            state.loaded = true;
            state.error = typeof action.payload === 'string' ? action.payload : action.error.message ?? 'Unable to update the game.';
        });
    }
});

export const {
    fadeOutContent,
    loadNextContent,
    showExtraContent,
    resetPointsDiff,
    updateOptionSelected,
    updateOptionAnswer,
    updateOrder,
    updateConnectSelectedPairs,
    updateText,
    updateNumberScale,
    clearError,
    setGameSession,
    setEndFormData
} = slice.actions;
export default slice.reducer;
